import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, MapPin, Sparkles } from "lucide-react";
import { api } from "../lib/apiClient";
import { resolveImageUrl } from "../lib/images";

const MONTHS_FR = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
];

function formatDateRange(startIso, endIso) {
    try {
        const s = new Date(startIso);
        const e = new Date(endIso);
        const sd = s.getDate();
        const ed = e.getDate();
        const month = MONTHS_FR[s.getMonth()].toLowerCase();
        if (sd === ed) return `le ${sd} ${month}`;
        return `du ${sd} au ${ed} ${month}`;
    } catch (_err) {
        return "";
    }
}

export default function Home() {
    const [eventsData, setEventsData] = useState({ events: [], month: new Date().getMonth() + 1 });
    const [cookieMonth, setCookieMonth] = useState(null);
    const [shops, setShops] = useState([]);

    const fetchAll = useCallback(async () => {
        try {
            const [evRes, cmRes, shRes] = await Promise.all([
                api.get("/events/current"),
                api.get("/cookie-of-the-month"),
                api.get("/shops"),
            ]);
            setEventsData(evRes.data);
            setCookieMonth(cmRes.data);
            setShops(shRes.data || []);
        } catch (_e) {
            // empty state will display
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const monthName = MONTHS_FR[(eventsData.month || 1) - 1];

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-[#3D1F0F] min-h-[calc(100vh-5rem)] flex items-center">
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-70"
                    style={{
                        backgroundImage:
                            "url('/images/hero-cookies.jpg')",
                    }}
                ></div>
                <div className="absolute inset-0 bg-gradient-to-r from-[#2A1408]/90 via-[#3D1F0F]/55 to-[#D6552C]/15"></div>

                <div className="relative w-full max-w-6xl mx-auto px-6 sm:px-10 py-12 sm:py-16 md:py-24 text-[#FFF1E0]">
                    <p className="font-handwritten text-2xl sm:text-3xl md:text-4xl text-[#C99889] mb-2 fade-up">
                        cookies, salon de thé & évènements
                    </p>
                    <h1
                        className="font-display text-[12vw] md:text-[10rem] leading-[0.85] tracking-[-0.04em] fade-up delay-1"
                        style={{ fontWeight: 800 }}
                    >
                        Le cookie
                        <br />
                        <span className="inline-block">qui</span>{" "}
                        <span className="inline-block scribble scribble-pop">claque.</span>
                    </h1>
                    <p className="mt-5 sm:mt-6 md:mt-8 max-w-xl text-base md:text-lg text-[#F5E6D3]/90 fade-up delay-2">
                        Fait main à Mâcon depuis 2021. Recettes généreuses, ingrédients
                        choisis, et désormais aussi à Bourg-en-Bresse.
                    </p>
                    <div className="mt-6 sm:mt-8 md:mt-10 flex flex-wrap items-center gap-4 sm:gap-5 fade-up delay-3">
                        <Link to="/carte" className="brand-btn brand-btn-pop" data-testid="hero-cta-carte">
                            Voir la carte <ArrowRight size={18} />
                        </Link>
                        <Link
                            to="/commander"
                            data-testid="hero-cta-order"
                            className="font-display text-base md:text-lg text-[#FFF1E0] hover:text-[#C99889] transition-colors"
                        >
                            ou commander direct →
                        </Link>
                    </div>
                </div>
            </section>

            {/* EVENEMENTS DU MOIS */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24">
                <div className="mb-14">
                    <p className="section-tag">à ne pas rater</p>
                    <div className="flex flex-wrap items-end gap-x-5 gap-y-2 mt-2">
                        <h2 className="font-display text-5xl md:text-6xl text-[#2A1408]">
                            Évènements du mois de
                        </h2>
                        <span
                            className="tilted-month text-7xl md:text-9xl md:ml-6 lg:ml-12"
                            data-testid="tilted-month-name"
                        >
                            {monthName}
                        </span>
                    </div>
                </div>

                {eventsData.events.length === 0 ? (
                    <div className="brand-card p-10 text-center max-w-2xl mx-auto">
                        <p className="font-handwritten text-4xl text-[#B07B66]">
                            Aucun évènement ce mois-ci
                        </p>
                        <p className="mt-3 text-[#5C3D2E]">
                            On prépare des belles surprises, revenez vite !
                        </p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                        {eventsData.events.map((ev, i) => (
                            <article
                                key={ev.id}
                                className={`brand-card p-7 ${i % 2 === 0 ? "tilt-l" : "tilt-r"} hover:!rotate-0`}
                                data-testid={`event-card-${ev.id}`}
                            >
                                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#B07B66] mb-3 font-bold">
                                    <Calendar size={14} strokeWidth={2.2} />
                                    {formatDateRange(ev.start_date, ev.end_date)}
                                </div>
                                <h3 className="font-display text-3xl text-[#2A1408] mb-2 leading-none">
                                    {ev.title}
                                </h3>
                                <p className="flex items-start gap-2 text-sm text-[#5C3D2E]">
                                    <MapPin size={16} strokeWidth={2} className="mt-0.5" />
                                    {ev.location}
                                </p>
                                {ev.description && (
                                    <p className="mt-3 text-[#3D1F0F]">{ev.description}</p>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </section>

            {/* COOKIE DU MOIS */}
            <section className="bg-grain-dark text-[#FFF1E0] relative overflow-hidden">
                <div className="hidden md:block absolute -top-8 right-12 sticker sticker-tilt-r">
                    <Sparkles size={14} className="inline mr-1" /> nouveau
                </div>
                <div className="max-w-6xl mx-auto px-6 sm:px-10 py-28 grid md:grid-cols-2 gap-14 items-center">
                    <div className="order-2 md:order-1">
                        <p className="font-handwritten text-3xl text-[#C99889] mb-2">
                            la pépite du moment
                        </p>
                        <h2 className="font-display text-6xl md:text-7xl leading-[0.85]">
                            Le cookie
                            <br />
                            <span className="text-[#C99889]">du mois.</span>
                        </h2>
                        {cookieMonth ? (
                            <>
                                <h3 className="mt-8 font-display text-3xl text-[#FFF1E0]">
                                    {cookieMonth.name}
                                </h3>
                                {cookieMonth.description && (
                                    <p className="mt-3 text-[#F5E6D3]/90 max-w-md">
                                        {cookieMonth.description}
                                    </p>
                                )}
                                {cookieMonth.price != null && (
                                    <p className="mt-4 font-display text-4xl text-[#C99889]">
                                        {Number(cookieMonth.price).toFixed(2).replace(".", ",")} €
                                    </p>
                                )}
                            </>
                        ) : (
                            <p className="mt-8 text-[#F5E6D3]/90 max-w-md">
                                Bientôt dévoilé. Restez gourmand·e.
                            </p>
                        )}
                        <Link
                            to="/carte"
                            className="brand-btn brand-btn-pop mt-8"
                            data-testid="cookie-of-month-cta"
                        >
                            Voir toute la carte <ArrowRight size={18} />
                        </Link>
                    </div>
                    <div className="order-1 md:order-2 relative">
                        {cookieMonth?.image_url && (
                            <img
                                src={resolveImageUrl(cookieMonth.image_url)}
                                alt={cookieMonth?.name || "Cookie du mois"}
                                className="rounded-2xl w-full h-[440px] object-cover border-4 border-[#FFF1E0] tilt-r"
                                style={{ boxShadow: "12px 12px 0 #B07B66" }}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* SHOPS PREVIEW */}
            <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
                    <div>
                        <p className="section-tag">venez nous voir</p>
                        <h2 className="font-display text-5xl md:text-6xl text-[#2A1408] mt-1">
                            Nos repaires.
                        </h2>
                    </div>
                    <Link
                        to="/boutiques"
                        className="brand-btn-outline"
                        data-testid="home-shops-cta"
                    >
                        Toutes les boutiques →
                    </Link>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                    {shops.map((s, idx) => (
                        <div key={s.slug || idx} className="brand-card overflow-hidden">
                            {s.image_url ? (
                                <img
                                    src={resolveImageUrl(s.image_url)}
                                    alt={s.name}
                                    className="w-full aspect-[4/3] object-cover"
                                />
                            ) : (
                                <div className="w-full aspect-[4/3] bg-[#E4D1BB] flex items-center justify-center text-[#B07B66]">
                                    <MapPin size={48} strokeWidth={1.5} />
                                </div>
                            )}
                            <div className="p-6">
                                <p className="text-xs uppercase tracking-widest text-[#B07B66] font-bold">
                                    Boutique {String(idx + 1).padStart(2, "0")}
                                </p>
                                <h3 className="font-display text-3xl text-[#2A1408]">
                                    {s.name}
                                </h3>
                                {s.address && (
                                    <p className="mt-2 text-[#5C3D2E]">{s.address}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
